import { useMemo } from "react";
import { type Shift } from "@/lib/backend/api";
import { isSettled, toEntryState } from "@/lib/shift/entry-status";
import { formatTime } from "@/utils/format-datetime";
import { calculateShiftDuration, minutesToDuration } from "@/utils/time";

export interface ShiftDisplayTime {
  /** Start time in HH:mm format */
  startTime: string;
  /** End time in HH:mm format */
  endTime: string;
  /** Break duration in minutes */
  breakMinutes: number | null;
  /** Total worked hours (after break deduction) */
  totalDuration: {
    hours: number;
    minutes: number;
  };
  /** Formatted time range: "09:00 - 17:00" */
  timeRange: string;
  /** Formatted duration in Dutch: "8u30m" */
  durationDutch: string;
  /** Formatted duration in English: "8h30m" */
  durationEnglish: string;
  /** Full formatted string: "09:00 - 17:00 • 8u30m" */
  fullDisplay: string;
  /** Whether actual worked hours differ from planned */
  hasActualHours: boolean;
  /** Whether the shift has been fully declared (start, end, and break) */
  isFullyDeclared: boolean;
  /**
   * Whether the hours are out of the employee's hands: the office accepted them, or they are
   * already moving through payroll.
   *
   * Deliberately a different question from {@link isFullyDeclared} — having filled the fields in is
   * not a reason to stop someone correcting them, and treating it as one is what locked employees
   * out of their own hours (#495).
   */
  isLocked: boolean;
}

/**
 * Statuses in which the office (or payroll) owns the hours line. Mirrors the api's
 * `assertAcceptedEditableByOffice` / `assertNotPayrollLocked`, so the app refuses exactly what the
 * server refuses instead of guessing something stricter. REJECTED is deliberately absent: a
 * rejected line is precisely the one the employee still needs to fix.
 *
 * Payroll progress is no longer a status: the legacy KLAARGEZET_VOOR_VERLONING/VERLOOND values were
 * dropped from the enum. A payroll-claimed line reads ACCEPTED (its payRunId-FK, which the app does
 * not receive, carries the claim), so locking on ACCEPTED covers payroll too.
 */
// Exactly the statuses `entry-status` calls settled, and stated once rather than twice: this
// list and that mapping drifting apart is how the app would end up locking a line it still
// shows a submit button for. With the legacy payroll values gone that resolves to ACCEPTED
// alone, which is what the list here used to spell out.
const isLockedStatus = (status: string) => isSettled(toEntryState(status));

/**
 * Hook that calculates display time for a shift card
 *
 * **Business Logic:**
 *
 * - If ShiftTimeEntry exists with actual times → use actual worked time
 * - Otherwise → use planned shift time
 * - Formats time in Dutch locale (8u30m) for display
 *
 * @param shift - The shift object
 * @returns Formatted time display data
 */
export function useShiftDisplayTime(shift: Shift): ShiftDisplayTime {
  return useMemo(() => {
    const timeEntry = shift.shiftTimeEntry;

    // Determine if we should use actual (time entry) or planned (shift) times
    const hasActualHours = !!(timeEntry?.startAt && timeEntry?.endAt);

    // Use actual times if available, otherwise fall back to planned times.
    // ShiftTimeEntry.startAt is typed as object | null in the generated API,
    // so we safely fall back to the shift's startAt when it's not a string.
    const rawStartAt = hasActualHours ? timeEntry?.startAt : shift.startAt;
    const rawEndAt = hasActualHours ? timeEntry?.endAt : shift.endAt;

    const startAt = typeof rawStartAt === "string" ? rawStartAt : shift.startAt;
    const endAt = typeof rawEndAt === "string" ? rawEndAt : shift.endAt;
    const breakMinutes = hasActualHours ? (timeEntry.breakTimeMinutes ?? null) : null;

    // Format times — altijd in projecttijd. Tempo's `format()` zonder tz leest
    // de klok van het toestel, waardoor een dienst van 15:00-23:00 op een
    // telefoon in Toronto als 9:00-17:00 verscheen.
    const startTime = formatTime(startAt);
    const endTime = formatTime(endAt);
    const timeRange = `${startTime} - ${endTime}`;

    // Calculate duration
    const totalDuration = calculateShiftDuration(startAt, endAt, breakMinutes ?? 0);

    // Format duration in different languages
    const durationDutch = formatDurationDutch(totalDuration.hours, totalDuration.minutes);
    const durationEnglish = formatDurationEnglish(totalDuration.hours, totalDuration.minutes);

    // Check if fully declared (has start, end, and break time)
    const isFullyDeclared = !!(
      timeEntry?.startAt &&
      timeEntry?.endAt &&
      timeEntry?.breakTimeMinutes !== null &&
      timeEntry?.breakTimeMinutes !== undefined
    );

    const isLocked = !!timeEntry?.status && isLockedStatus(timeEntry.status);

    // Full display with bullet separator
    const fullDisplay = `${timeRange} • ${durationDutch}`;

    return {
      startTime,
      endTime,
      breakMinutes,
      totalDuration,
      timeRange,
      durationDutch,
      durationEnglish,
      fullDisplay,
      hasActualHours,
      isFullyDeclared,
      isLocked,
    };
  }, [shift]);
}

/** Formats duration in Dutch: "8u30m" Special case: If 0 minutes, return only hours: "8u" */
function formatDurationDutch(hours: number, minutes: number): string {
  if (minutes === 0) {
    return `${hours}u`;
  }
  return `${hours}u${minutes}m`;
}

/** Formats duration in English: "8h30m" Special case: If 0 minutes, return only hours: "8h" */
function formatDurationEnglish(hours: number, minutes: number): string {
  if (minutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h${minutes}m`;
}

/** Hook variant that also returns break time information for detailed displays */
export function useShiftDisplayTimeDetailed(shift: Shift) {
  const displayTime = useShiftDisplayTime(shift);

  const breakDisplay = useMemo(() => {
    if (displayTime.breakMinutes === null) {
      return null;
    }

    const breakDuration = minutesToDuration(displayTime.breakMinutes);
    return {
      minutes: displayTime.breakMinutes,
      formatted: formatDurationDutch(breakDuration.hours, breakDuration.minutes),
      formattedEnglish: formatDurationEnglish(breakDuration.hours, breakDuration.minutes),
    };
  }, [displayTime.breakMinutes]);

  return {
    ...displayTime,
    breakDisplay,
  };
}
